import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { liveAPI, API_BASE } from '../api/client';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import Hls from 'hls.js';
import { Play, Users, Sparkles, Volume2, VolumeX, ShieldCheck, Radio, Tv, ArrowRight, ArrowLeft } from 'lucide-react';
import { getCurrentLanguage, isRTL } from '../utils/translations';
import { getUserRegionalPreferences } from '../utils/countryLanguageEngine';

const LIVE_WIDGET_I18N = {
    ar: {
        liveNow: "مباشر الآن",
        viewers: "مشاهد",
        officialMgmt: "إدارة لوكسفونو الرسمية",
        defaultTitle: "بث العروض والمقتنيات الفاخرة المباشر",
        defaultDesc: "عروض حية حصرية وتخفيضات استثنائية مع إمكانية الشراء الفوري أثناء البث.",
        joinCta: "انضم للبث الحي الفوري وتسوّق الآن",
        sectionTitle: "مسرح البث المباشر الفاخر",
        sectionSub: "تسوّق مع الخبراء بالموعد الفعلي",
        liveShowcase: "LIVE SHOWCASE"
    },
    en: {
        liveNow: "LIVE NOW",
        viewers: "viewers",
        officialMgmt: "LUXVONO Official Host",
        defaultTitle: "Live Luxury Showcase & Curations",
        defaultDesc: "Exclusive live showcases and exceptional curations with instant in-stream checkout.",
        joinCta: "Join Live Stream & Shop Now",
        sectionTitle: "LUXVONO Live Stage",
        sectionSub: "Shop with experts in real time",
        liveShowcase: "LIVE SHOWCASE"
    },
    fr: {
        liveNow: "EN DIRECT",
        viewers: "spectateurs",
        officialMgmt: "Direction Officielle LUXVONO",
        defaultTitle: "Diffusion en Direct des Pièces de Luxe",
        defaultDesc: "Présentations exclusives et offres privilégiées avec achat instantané pendant le direct.",
        joinCta: "Rejoindre le Direct & Acheter",
        sectionTitle: "Scène en Direct LUXVONO",
        sectionSub: "Achetez avec nos experts en temps réel",
        liveShowcase: "DIRECT EXCLUSIF"
    },
    de: {
        liveNow: "JETZT LIVE",
        viewers: "Zuschauer",
        officialMgmt: "Offizielle LUXVONO-Leitung",
        defaultTitle: "Live-Präsentation erlesener Luxusgüter",
        defaultDesc: "Exklusive Live-Angebote mit sofortiger Kaufmöglichkeit während der Übertragung.",
        joinCta: "Jetzt Live beitreten & Einkaufen",
        sectionTitle: "LUXVONO Live-Bühne",
        sectionSub: "Shoppen Sie in Echtzeit mit Experten",
        liveShowcase: "LIVE-SHOWCASE"
    },
    es: {
        liveNow: "EN VIVO",
        viewers: "espectadores",
        officialMgmt: "Administración Oficial LUXVONO",
        defaultTitle: "Transmisión en Vivo de Artículos de Lujo",
        defaultDesc: "Presentaciones exclusivas y ofertas excepcionales con compra inmediata durante la transmisión.",
        joinCta: "Únete al En Vivo y Compra Ahora",
        sectionTitle: "Escenario en Vivo LUXVONO",
        sectionSub: "Compra con expertos en tiempo real",
        liveShowcase: "EN VIVO"
    },
    it: {
        liveNow: "IN DIRETTA",
        viewers: "spettatori",
        officialMgmt: "Gestione Ufficiale LUXVONO",
        defaultTitle: "Vetrina dal Vivo di Oggetti di Lusso",
        defaultDesc: "Vetrina esclusiva con possibilità di acquisto istantaneo durante la diretta.",
        joinCta: "Entra nella Diretta e Acquista Ora",
        sectionTitle: "Palco dal Vivo LUXVONO",
        sectionSub: "Acquista con gli esperti in tempo reale",
        liveShowcase: "IN DIRETTA"
    },
    ru: {
        liveNow: "В ЭФИРЕ",
        viewers: "зрителей",
        officialMgmt: "Официальное руководство LUXVONO",
        defaultTitle: "Прямой эфир эксклюзивных предметов роскоши",
        defaultDesc: "Эксклюзивные живые показы с возможностью мгновенной покупки прямо во время эфира.",
        joinCta: "Присоединиться к эфиру и купить",
        sectionTitle: "Сцена прямых трансляций LUXVONO",
        sectionSub: "Покупки с экспертами в режиме реального времени",
        liveShowcase: "ПРЯМОЙ ЭФИР"
    },
    tr: {
        liveNow: "CANLI YAYIN",
        viewers: "izleyici",
        officialMgmt: "Resmi LUXVONO Yönetimi",
        defaultTitle: "Canlı Lüks Ürün ve Koleksiyon Yayını",
        defaultDesc: "Yayın esnasında anında satın alma imkanı sunan özel canlı tanıtımlar ve koleksiyonlar.",
        joinCta: "Canlı Yayına Katıl ve Alışverişe Başla",
        sectionTitle: "LUXVONO Canlı Sahnesi",
        sectionSub: "Uzmanlarla eşzamanlı canlı alışveriş",
        liveShowcase: "CANLI YAYIN"
    },
    zh: {
        liveNow: "正在直播",
        viewers: "位观众",
        officialMgmt: "LUXVONO 官方主播",
        defaultTitle: "尊贵甄品即时直播展厅",
        defaultDesc: "独家直播甄选与尊享优惠，支持直播间即时下单结账。",
        joinCta: "立即进入直播间挑选购买",
        sectionTitle: "LUXVONO 奢华直播展厅",
        sectionSub: "与专家实时互动，尊享非凡体验",
        liveShowcase: "甄品直播"
    },
    ja: {
        liveNow: "ライブ配信中",
        viewers: "人が視聴中",
        officialMgmt: "LUXVONO 公式配信",
        defaultTitle: "高級コレクション ライブ配信",
        defaultDesc: "配信中に即時購入可能な限定ライブ紹介と特別なキュレーション。",
        joinCta: "ライブに参加して今すぐショッピング",
        sectionTitle: "LUXVONO ライブステージ",
        sectionSub: "エキスパートとリアルタイムでショッピング",
        liveShowcase: "ライブ配信"
    },
    ko: {
        liveNow: "라이브 방송 중",
        viewers: "명 시청 중",
        officialMgmt: "LUXVONO 공식 진행",
        defaultTitle: "럭셔리 컬렉션 라이브 쇼케이스",
        defaultDesc: "방송 중 즉시 구매가 가능한 독점 라이브 쇼케이스 및 특별 혜택.",
        joinCta: "라이브 입장 후 바로 쇼핑하기",
        sectionTitle: "LUXVONO 라이브 스테이지",
        sectionSub: "전문가와 함께하는 실시간 라이브 쇼핑",
        liveShowcase: "라이브 쇼케이스"
    },
    so: {
        liveNow: "TOOS HADDA",
        viewers: "daawadayaal",
        officialMgmt: "Maamulka Rasmiga ee LUXVONO",
        defaultTitle: "Bandhigga Tooska ah ee Alaabta Qaliga ah",
        defaultDesc: "Bandhigyo toos ah oo khaas ah iyo iibsi degdeg ah inta baahintu socoto.",
        joinCta: "Ku Biir Baahinta Tooska ah oo Hadda Iibso",
        sectionTitle: "Masraxa Tooska ah ee LUXVONO",
        sectionSub: "Kala dukaamayso khubarada xilliga dhabta ah",
        liveShowcase: "BAAHIN TOOS AH"
    },
    am: {
        liveNow: "አሁን በቀጥታ",
        viewers: "ተመልካቾች",
        officialMgmt: "የLUXVONO ይፋዊ አስተዳዳሪ",
        defaultTitle: "የቀጥታ የቅንጦት እቃዎች ማሳያ",
        defaultDesc: "በስርጭቱ ወቅት ፈጣን ግዢ መፈጸም የሚችሉበት ልዩ የቀጥታ ማሳያ።",
        joinCta: "ወደ ቀጥታ ስርጭቱ ይግቡና አሁኑኑ ይሸምቱ",
        sectionTitle: "የLUXVONO የቅንጦት ቀጥታ መድረክ",
        sectionSub: "ከባለሙያዎች ጋር በቀጥታ ይሸምቱ",
        liveShowcase: "ቀጥታ ስርጭት"
    },
    gdq: {
        liveNow: "مباشر الآن",
        viewers: "مشاهد",
        officialMgmt: "إدارة لوكسفونو الرسمية",
        defaultTitle: "بث العروض والمقتنيات الفاخرة المباشر",
        defaultDesc: "عروض حية حصرية وتخفيضات استثنائية مع إمكانية الشراء الفوري أثناء البث.",
        joinCta: "انضم للبث الحي الفوري وتسوّق الآن",
        sectionTitle: "مسرح البث المباشر الفاخر",
        sectionSub: "تسوّق مع الخبراء بالموعد الفعلي باللسان المهري",
        liveShowcase: "LIVE SHOWCASE"
    }
};

const LiveStreamCard = ({ room, onJoinRoom, widgetStrings }) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const [isMuted, setIsMuted] = useState(true);
    const [hasVideoError, setHasVideoError] = useState(false);

    useEffect(() => {
        if (!room || !videoRef.current) return;

        const baseUrl = API_BASE || 'https://api.luxvono.com';
        const hlsUrl = room.hlsUrl || `${baseUrl}/media/hls/${room.streamKey}.m3u8`;

        if (videoRef.current) {
            try {
                videoRef.current.muted = true;
                videoRef.current.defaultMuted = true;
                videoRef.current.playsInline = true;
                videoRef.current.setAttribute('playsinline', '');
                videoRef.current.setAttribute('webkit-playsinline', '');
                videoRef.current.setAttribute('x5-playsinline', '');
            } catch (_) {}
        }

        if (Hls.isSupported()) {
            if (hlsRef.current) hlsRef.current.destroy();
            const hls = new Hls({
                liveSyncDurationCount: 3,
                liveMaxLatencyDurationCount: 8,
                maxLiveSyncPlaybackRate: 1.2,
                maxBufferLength: 8,
                maxMaxBufferLength: 16,
                lowLatencyMode: true,
                enableWorker: false,
                manifestLoadingMaxRetry: 15,
                manifestLoadingRetryDelay: 500,
                levelLoadingMaxRetry: 15,
                levelLoadingRetryDelay: 500,
            });
            hls.loadSource(hlsUrl);
            hls.attachMedia(videoRef.current);
            hlsRef.current = hls;

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (videoRef.current) {
                    videoRef.current.muted = true;
                    videoRef.current.play().catch(() => {});
                }
            });
            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    setHasVideoError(true);
                }
            });
        } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
            videoRef.current.src = hlsUrl;
            videoRef.current.muted = true;
            videoRef.current.addEventListener('loadedmetadata', () => {
                videoRef.current?.play().catch(() => {});
            });
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [room?.id, room?.streamKey, room?.hlsUrl]);

    const toggleMute = (e) => {
        e.stopPropagation();
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
        }
    };

    return (
        <div 
            className="lux-live-stream-card"
            onClick={() => onJoinRoom(room.id)}
            style={{
                position: 'relative',
                width: '100%',
                borderRadius: '24px',
                overflow: 'hidden',
                background: 'linear-gradient(145deg, #111827 0%, #000000 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
                cursor: 'pointer',
                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Top Video Preview & Badges */}
            <div style={{ position: 'relative', width: '100%', height: '320px', background: '#05060A', overflow: 'hidden' }}>
                {/* Background Poster / Luxury Placeholder */}
                <div 
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: room.coverImage ? `url(${room.coverImage})` : 'radial-gradient(circle at center, #231C38 0%, #08090D 80%)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1
                    }}
                >
                    {!room.coverImage && (
                        <div style={{ textAlign: 'center', opacity: 0.85 }}>
                            <div style={{
                                width: '70px',
                                height: '70px',
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.08)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 12px',
                                boxShadow: '0 0 25px rgba(255, 255, 255, 0.1)'
                            }}>
                                <Play size={32} fill="#ffffff" color="#ffffff" style={{ marginLeft: '4px' }} />
                            </div>
                            <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: '700', letterSpacing: '2px' }}>
                                LUXVONO LIVE STUDIO
                            </span>
                        </div>
                    )}
                </div>

                {/* HLS Video Stream Element */}
                {!hasVideoError && (
                    <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline 
                        muted 
                        loop 
                        style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: 2
                        }}
                    />
                )}

                {/* Cinematic Vignette Overlay */}
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to top, rgba(10, 11, 16, 0.95) 0%, rgba(10, 11, 16, 0.2) 50%, rgba(0, 0, 0, 0.65) 100%)',
                    pointerEvents: 'none',
                    zIndex: 3
                }} />

                {/* Top Floating Pills */}
                <div style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    right: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    zIndex: 4
                }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {/* Live Pulsing Badge */}
                        <div style={{
                            background: 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)',
                            color: '#FFFFFF',
                            fontSize: '11px',
                            fontWeight: '800',
                            padding: '6px 14px',
                            borderRadius: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 0 15px rgba(239, 68, 68, 0.5)',
                            letterSpacing: '1px'
                        }}>
                            <span style={{
                                width: '7px',
                                height: '7px',
                                borderRadius: '50%',
                                background: '#FFFFFF',
                                animation: 'luxPulse 1.5s infinite'
                            }} />
                            {widgetStrings?.liveNow || 'LIVE'}
                        </div>

                        {/* Viewers Pill */}
                        <div style={{
                            background: 'rgba(0, 0, 0, 0.65)',
                            backdropFilter: 'blur(12px)',
                            border: '1px solid rgba(255, 255, 255, 0.12)',
                            color: '#FFFFFF',
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '6px 12px',
                            borderRadius: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <Users size={12} color="#ffffff" />
                            <span>{Number(room.participantCount || 0)} {widgetStrings?.viewers || 'viewers'}</span>
                        </div>
                    </div>

                    {/* Mute Toggle Button */}
                    <button 
                        onClick={toggleMute}
                        style={{
                            background: 'rgba(0, 0, 0, 0.65)',
                            backdropFilter: 'blur(12px)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: '#FFFFFF'
                        }}
                    >
                        {isMuted ? <VolumeX size={15} color="#EF4444" /> : <Volume2 size={15} color="#ffffff" />}
                    </button>
                </div>
            </div>

            {/* Bottom Content & Interactive CTA Bar */}
            <div style={{
                padding: '20px 24px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                background: '#0D0E15'
            }}>
                {/* Host Info Line */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px'
                    }}>
                        👑
                    </div>
                    <span style={{ color: '#E2E8F0', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {widgetStrings?.officialMgmt || 'LUXVONO Official'}
                        <ShieldCheck size={14} color="#10B981" />
                    </span>
                </div>

                {/* Title & Description */}
                <div>
                    <h3 style={{
                        color: '#FFFFFF',
                        fontSize: '18px',
                        fontWeight: '800',
                        margin: '0 0 6px 0',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                        {room.title || widgetStrings?.defaultTitle}
                    </h3>
                    <p style={{
                        color: '#94A3B8',
                        fontSize: '13px',
                        margin: 0,
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                        {room.description || widgetStrings?.defaultDesc}
                    </p>
                </div>

                {/* Action Button */}
                <div style={{
                    marginTop: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: '#e02424',
                    color: '#ffffff',
                    padding: '12px 20px',
                    borderRadius: '14px',
                    fontWeight: '800',
                    fontSize: '14px',
                    boxShadow: '0 6px 20px rgba(224, 36, 36, 0.35)',
                    transition: 'all 0.25s ease'
                }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Play size={16} fill="#ffffff" />
                        {widgetStrings?.joinCta || 'Join Live Stream'}
                    </span>
                    <span style={{ fontSize: '16px' }}>⚡</span>
                </div>
            </div>
        </div>
    );
};

export default function LiveHomepageWidget({ onJoinRoom }) {
    const navigate = useNavigate();
    const [activeRooms, setActiveRooms] = useState([]);
    const currentLang = getCurrentLanguage() || 'ar';
    const rtl = isRTL();
    const widgetStrings = LIVE_WIDGET_I18N[currentLang] || LIVE_WIDGET_I18N['en'] || LIVE_WIDGET_I18N['ar'];

    const fetchLiveRooms = async () => {
        try {
            const userGeo = getUserRegionalPreferences();
            const clientCountry = (userGeo?.country || 'SA').toUpperCase();
            const data = await liveAPI.getActiveRooms(clientCountry);
            const active = (data || []).filter(r => {
                const isActive = r.status === 'ACTIVE' || r.status === 'PREVIEW' || !r.status;
                if (!isActive) return false;
                if (!r.availableCountries || r.availableCountries.length === 0) return true; // Global stream
                const countriesList = r.availableCountries.map(c => String(c).toUpperCase());
                return countriesList.includes('ALL') || countriesList.includes(clientCountry);
            });
            setActiveRooms(prev => JSON.stringify(prev) === JSON.stringify(active) ? prev : active);
        } catch (e) {
            console.error('Failed fetching live rooms', e);
        }
    };

    useEffect(() => {
        fetchLiveRooms();
        const interval = setInterval(fetchLiveRooms, 5000);

        const handleGeoChange = () => fetchLiveRooms();
        window.addEventListener('geoContextChange', handleGeoChange);

        const stompClient = new Client({
            webSocketFactory: () => new SockJS(`${API_BASE || ''}/ws/chat`),
            onConnect: () => {
                stompClient.subscribe('/topic/live-rooms', fetchLiveRooms);
            }
        });
        stompClient.activate();

        return () => {
            clearInterval(interval);
            stompClient.deactivate();
            window.removeEventListener('geoContextChange', handleGeoChange);
        };
    }, []);

    const hasActiveRooms = Array.isArray(activeRooms) && activeRooms.length > 0;

    const handleEnterPortal = () => {
        if (typeof onJoinRoom === 'function' && activeRooms.length > 0) {
            onJoinRoom(activeRooms[0].id);
        } else {
            navigate('/live');
        }
    };

    return (
        <section 
            className="luxvono-live-homepage-section container"
            dir={rtl ? 'rtl' : 'ltr'}
            style={{
                margin: '28px auto 36px',
                position: 'relative',
                zIndex: 10
            }}
        >
            <style dangerouslySetInnerHTML={{__html: `
                @keyframes luxPulse {
                    0% { transform: scale(0.95); opacity: 0.8; }
                    50% { transform: scale(1.2); opacity: 1; }
                    100% { transform: scale(0.95); opacity: 0.8; }
                }
                .lux-live-stream-card:hover {
                    transform: translateY(-6px);
                    border-color: #e02424 !important;
                    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.7), 0 0 35px rgba(224, 36, 36, 0.25) !important;
                }
                .lux-live-preview-stage {
                    background: linear-gradient(135deg, #0D0E15 0%, #151824 50%, #0B0C12 100%);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    border-radius: 24px;
                    padding: 40px 32px;
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 20px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    gap: 20px;
                }
                .lux-live-preview-stage::before {
                    content: '';
                    position: absolute;
                    top: -60px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 320px;
                    height: 140px;
                    background: radial-gradient(ellipse at center, rgba(239, 68, 68, 0.2) 0%, transparent 70%);
                    pointer-events: none;
                }
            `}} />

            {/* Section Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '14px',
                borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
                paddingBottom: '12px',
                flexWrap: 'wrap',
                gap: '12px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: '#EF4444',
                        animation: 'luxPulse 1.5s infinite',
                        boxShadow: '0 0 12px #EF4444'
                    }} />
                    <h2 style={{
                        color: 'var(--text, #111827)',
                        fontSize: '21px',
                        fontWeight: '800',
                        margin: 0,
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        {widgetStrings.sectionTitle}
                        <span style={{
                            background: 'rgba(239, 68, 68, 0.12)',
                            border: '1px solid rgba(239, 68, 68, 0.28)',
                            color: '#EF4444',
                            fontSize: '11px',
                            padding: '2px 10px',
                            borderRadius: '20px',
                            fontWeight: '700'
                        }}>
                            {widgetStrings.liveShowcase}
                        </span>
                    </h2>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ color: 'var(--text-muted, #64748B)', fontSize: '13px', fontWeight: '600' }}>
                        {widgetStrings.sectionSub}
                    </span>
                    <Link
                        to="/live"
                        style={{
                            color: '#FE2C55',
                            fontSize: '13px',
                            fontWeight: '700',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            borderRadius: '999px',
                            background: 'rgba(254, 44, 85, 0.08)',
                            border: '1px solid rgba(254, 44, 85, 0.2)'
                        }}
                    >
                        <span>{rtl ? 'استعراض كافة غرف البث ➔' : 'Explore All Live Rooms ➔'}</span>
                    </Link>
                </div>
            </div>

            {/* Active Live Rooms or High-End Live Stage Preview */}
            {hasActiveRooms ? (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: activeRooms.length > 1 ? 'repeat(auto-fit, minmax(360px, 1fr))' : 'minmax(320px, 800px)',
                    justifyContent: 'center',
                    gap: '28px'
                }}>
                    {activeRooms.map(room => (
                        <LiveStreamCard 
                            key={room.id} 
                            room={room} 
                            onJoinRoom={onJoinRoom} 
                            widgetStrings={widgetStrings}
                        />
                    ))}
                </div>
            ) : (
                <div className="lux-live-preview-stage">
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 16px',
                        borderRadius: '24px',
                        background: 'rgba(239, 68, 68, 0.15)',
                        border: '1px solid rgba(239, 68, 68, 0.35)',
                        color: '#EF4444',
                        fontSize: '12px',
                        fontWeight: '800',
                        letterSpacing: '1px'
                    }}>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#EF4444',
                            animation: 'luxPulse 1.2s infinite'
                        }} />
                        <span>{rtl ? 'استوديو البث المباشر الفاخر ✦ LIVE COMMERCE' : 'LUXURY LIVE COMMERCE STAGE ✦'}</span>
                    </div>

                    <h3 style={{
                        color: '#FFFFFF',
                        fontSize: '26px',
                        fontWeight: '800',
                        margin: 0,
                        maxWidth: '750px',
                        lineHeight: 1.4
                    }}>
                        {widgetStrings.defaultTitle}
                    </h3>

                    <p style={{
                        color: '#A0AEC0',
                        fontSize: '15px',
                        maxWidth: '680px',
                        margin: 0,
                        lineHeight: 1.6
                    }}>
                        {widgetStrings.defaultDesc}
                    </p>

                    {/* Value propositions */}
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: '12px',
                        margin: '6px 0 10px 0'
                    }}>
                        <span style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '20px',
                            padding: '6px 14px',
                            fontSize: '12.5px',
                            color: '#E2E8F0',
                            fontWeight: '600'
                        }}>
                            💎 {rtl ? 'استعراض حي للمقتنيات الملكية' : 'Live Royal Curations'}
                        </span>
                        <span style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '20px',
                            padding: '6px 14px',
                            fontSize: '12.5px',
                            color: '#E2E8F0',
                            fontWeight: '600'
                        }}>
                            ⚡ {rtl ? 'شراء فوري مع كفالة الأصالة 100%' : 'Instant Checkout with 100% Escrow'}
                        </span>
                        <span style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '20px',
                            padding: '6px 14px',
                            fontSize: '12.5px',
                            color: '#E2E8F0',
                            fontWeight: '600'
                        }}>
                            🎙️ {rtl ? 'تواصل وتفاوض مباشر مع الخبراء' : 'Direct Interaction with Curators'}
                        </span>
                    </div>

                    {/* CTAs */}
                    <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                            onClick={handleEnterPortal}
                            style={{
                                background: 'linear-gradient(135deg, #FE2C55 0%, #D91B42 100%)',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '12px',
                                padding: '12px 28px',
                                fontSize: '14.5px',
                                fontWeight: '800',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 8px 24px rgba(254, 44, 85, 0.4)',
                                transition: 'all 0.25s ease'
                            }}
                        >
                            <Radio size={16} />
                            <span>{widgetStrings.joinCta || (rtl ? 'انضم لمسرح البث الحي الفوري وتسوّق الآن' : 'Join Live Stream & Shop Now')}</span>
                        </button>

                        <button
                            onClick={() => navigate('/live-rooms')}
                            style={{
                                background: 'rgba(255, 255, 255, 0.08)',
                                color: '#FFFFFF',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '12px',
                                padding: '12px 22px',
                                fontSize: '14px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.25s ease'
                            }}
                        >
                            <Tv size={16} />
                            <span>{rtl ? 'جدول العروض والقاعات' : 'Live Schedule & Channels'}</span>
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
